import {
  Image,
  Pressable,
  Text,
  View,
  useWindowDimensions,
  type ImageSourcePropType,
} from "react-native";
import type { ReactNode } from "react";
import { Pencil, Plus } from "lucide-react-native";
import {
  MetaText,
  PrimaryTitleText,
  MetricValueText,
  cardSurface,
  compactSecondarySurfaceStyle,
} from "@louise/moon-ui";
import { semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { OrganicMiniIcon, type OrganicMiniIconKind } from "./OrganicMiniIcon";

export type OutcomeDotFunnelStage = {
  id: string;
  label: string;
  value: number;
  displayValue?: string;
  dotSegments?: {
    total: number;
    active: number;
    color: string;
  }[];
  detail: string;
  missing?: boolean;
  icon?: OrganicMiniIconKind;
  illustration?: ImageSourcePropType;
  actionLabel?: string;
  onPress?: () => void;
  trailing?: ReactNode;
  hideDots?: boolean;
  // 0-1 probability, drives the bar length when hideDots is true.
  progressValue?: number;
};

function ProbabilityBar({
  value,
  width = 88,
  dimmed = false,
}: {
  value: number;
  width?: number;
  dimmed?: boolean;
}) {
  const clamped = Math.max(0, Math.min(1, value));
  return (
    <View
      style={{
        width,
        height: 10,
        borderRadius: 999,
        backgroundColor: "rgba(92,52,32,0.1)",
        overflow: "hidden",
        opacity: dimmed ? 0.64 : 1,
      }}
    >
      <View
        style={{
          width: `${clamped * 100}%`,
          height: "100%",
          backgroundColor: "#B76545",
        }}
      />
    </View>
  );
}

function EggDotGrid({
  total,
  active,
  stageIndex,
  segments,
  dimmed = false,
  width = 88,
}: {
  total: number;
  active: number;
  stageIndex: number;
  segments?: OutcomeDotFunnelStage["dotSegments"];
  dimmed?: boolean;
  width?: number;
}) {
  const dots = segments
    ? segments.flatMap((segment) =>
        Array.from({ length: Math.max(segment.total, 0) }).map((_, index) => ({
          active: index < segment.active,
          color: segment.color,
        })),
      )
    : Array.from({ length: total }).map((_, index) => ({
        active: index < active,
        color:
          stageIndex <= 1 ? "#F4B34F" : stageIndex <= 3 ? "#B76545" : "#6B5548",
      }));

  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 4,
        width,
        maxWidth: width,
        alignContent: "flex-start",
        justifyContent: "flex-start",
        opacity: dimmed ? 0.64 : 1,
      }}
    >
      {dots.map((dot, index) => {
        const isActive = dot.active;
        return (
          <View
            key={index}
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              backgroundColor: isActive ? dot.color : "rgba(107,85,72,0.1)",
              borderWidth: isActive ? 0 : 1,
              borderColor: "rgba(107,85,72,0.16)",
              opacity: isActive ? 1 : 0.42,
            }}
          />
        );
      })}
    </View>
  );
}

function OutcomeFunnelAction({
  missing,
  label,
}: {
  missing?: boolean;
  label?: string;
}) {
  const t = useTranslations();
  const resolvedLabel =
    label ??
    (missing
      ? t("homepage.outcome_dot_funnel_rows.action_add")
      : t("homepage.outcome_dot_funnel_rows.action_edit"));
  const Icon = missing ? Plus : Pencil;

  return (
    <View
      style={[
        compactSecondarySurfaceStyle,
        {
          minWidth: 54,
          height: 30,
          paddingHorizontal: 10,
          borderRadius: 999,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          flexShrink: 0,
        },
      ]}
    >
      <Icon size={12} color={semanticColors.text.primary} strokeWidth={1.9} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          lineHeight: 13,
          color: semanticColors.text.primary,
          fontWeight: "800",
        }}
      >
        {resolvedLabel}
      </Text>
    </View>
  );
}

export function OutcomeDotFunnelRows({
  stages,
  totalDots,
  embedded = false,
  attachedTop = false,
  stageIndexOffset = 0,
}: {
  stages: OutcomeDotFunnelStage[];
  totalDots?: number;
  /** Render rows inside an existing card without creating a nested card. */
  embedded?: boolean;
  /** Flatten the top edge when tabs are directly attached above the funnel. */
  attachedTop?: boolean;
  /**
   * Where these rows sit in the funnel as a whole.
   *
   * The dots darken with depth, and the band is chosen from a row's index — so
   * a caller rendering the funnel in two lists restarted the progression at
   * zero and shifted every colour by one step. It passes the offset instead.
   */
  stageIndexOffset?: number;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("OutcomeDotFunnelRows");
  const { width } = useWindowDimensions();
  const compact = width < 520;
  if (stages.length === 0) return null;

  const resolvedTotalDots = Math.max(totalDots ?? stages[0]?.value ?? 1, 1);

  return (
    <View
      style={
        embedded
          ? { gap: 6 }
          : {
              ...cardSurface(),
              ...(attachedTop
                ? {
                    borderTopWidth: 0,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                  }
                : null),
              padding: 8,
              gap: 6,
            }
      }
    >
      {stages.map((stage, index) => {
        const canAct = Boolean(stage.onPress);
        const compactIllustrated = compact && Boolean(stage.illustration);
        const compactIllustratedWaiting = compactIllustrated && stage.missing;
        const showDotGrid = !compactIllustratedWaiting;
        const rowStyle = {
          flexDirection: "row" as const,
          alignItems: "center" as const,
          gap: compactIllustratedWaiting ? 4 : compactIllustrated ? 8 : 12,
          paddingHorizontal: 8,
          paddingVertical: 10,
          borderBottomWidth: index < stages.length - 1 ? 1 : 0,
          borderBottomColor: semanticColors.border.subtle,
        };
        const dotGrid = stage.hideDots ? (
          <ProbabilityBar
            value={stage.progressValue ?? 0}
            width={compactIllustrated ? 56 : 88}
            dimmed={stage.missing}
          />
        ) : (
          <EggDotGrid
            total={resolvedTotalDots}
            active={stage.value}
            stageIndex={stageIndexOffset + index}
            segments={stage.dotSegments}
            dimmed={stage.missing}
            width={compactIllustrated ? 56 : 88}
          />
        );
        const trailing = stage.trailing ? (
          <View style={{ flexShrink: 0 }}>{stage.trailing}</View>
        ) : null;
        const content = (
          <>
            {stage.illustration ? (
              <Image
                source={stage.illustration}
                resizeMode="contain"
                style={{
                  width: compactIllustrated ? 28 : 36,
                  height: compactIllustrated ? 28 : 36,
                  flexShrink: 0,
                  marginRight: compactIllustratedWaiting ? 12 : 0,
                }}
              />
            ) : stage.icon ? (
              <View
                style={{
                  width: 36,
                  height: 36,
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <OrganicMiniIcon kind={stage.icon} size={34} />
              </View>
            ) : null}

            {!compactIllustratedWaiting ? (
              <View
                style={{
                  width: compactIllustrated ? 28 : 38,
                  alignSelf: "stretch",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {!stage.missing ? (
                  <MetricValueText style={{ fontSize: 18, lineHeight: 22 }}>
                    {stage.displayValue ?? stage.value}
                  </MetricValueText>
                ) : null}
              </View>
            ) : null}
            <View style={{ flex: 1, minWidth: 0, justifyContent: "center" }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                <PrimaryTitleText numberOfLines={2}>
                  {stage.label}
                </PrimaryTitleText>
              </View>
              <MetaText style={{ marginTop: 1 }}>{stage.detail}</MetaText>
            </View>
            {compact && trailing ? (
              <View
                style={{
                  width: 104,
                  flexShrink: 0,
                  alignItems: "flex-end",
                  gap: 6,
                }}
              >
                {showDotGrid ? dotGrid : null}
                {trailing}
              </View>
            ) : (
              <>
                {showDotGrid ? dotGrid : null}
                {trailing}
              </>
            )}
            {canAct ? (
              <OutcomeFunnelAction
                missing={stage.missing}
                label={stage.actionLabel}
              />
            ) : null}
          </>
        );

        if (stage.onPress) {
          const stageOnPress = stage.onPress;
          return (
            <Pressable
              key={stage.id}
              onPress={() => {
                capturePress(pressLabel(stage.label), stage.id);
                stageOnPress();
              }}
              accessibilityRole="button"
              accessibilityLabel={t(
                "homepage.outcome_dot_funnel_rows.add_stage",
                { params: { label: stage.label.toLowerCase() } },
              )}
              style={({ pressed }) => ({
                ...rowStyle,
                opacity: pressed ? 0.74 : 1,
                transform: [{ scale: pressed ? 0.996 : 1 }],
              })}
            >
              {content}
            </Pressable>
          );
        }

        return (
          <View key={stage.id} style={rowStyle}>
            {content}
          </View>
        );
      })}
    </View>
  );
}
